import { Link } from 'react-router-dom';

export interface HeroBadge {
  icon: string;
  label: string;
}

export interface HeroContent {
  eyebrow: string;
  headlineLines: string[];
  subheadline: string;
  primaryCTA: { text: string; link: string };
  secondaryCTA: { text: string; href: string };
  badges: HeroBadge[];
}

interface Props {
  content: HeroContent;
}

const HeroSection = ({ content }: Props) => {
  const { eyebrow, headlineLines, subheadline, primaryCTA, secondaryCTA, badges } = content;

  return (
    <section className="relative w-full min-h-screen flex overflow-hidden">

      {/* ── GLOBAL BASE LAYER ───────────────────────────────── */}
      <div className="absolute inset-0 z-0" style={{ background: 'linear-gradient(108deg, #040a12 0%, #081220 60%, #0d1b2a 100%)' }} />

      {/* ── FULL BG IMAGE (clipped/overlaid by panels) ─────── */}
      <div className="absolute inset-0 z-[1]">
        <img
          src="https://public.readdy.ai/ai/img_res/bbee8da8e39c1ee2ad7c022847c0c920.jpg"
          alt=""
          aria-hidden="true"
          className="w-full h-full object-cover object-center"
          style={{ filter: 'brightness(0.22) contrast(1.15) saturate(0.55)' }}
        />
      </div>

      {/* ── SPLIT OVERLAY GEOMETRY ──────────────────────────── */}
      {/* Left dark panel — covers left 60% */}
      <div
        className="absolute inset-0 z-[2] hidden lg:block"
        style={{ background: 'linear-gradient(96deg, rgba(4,10,18,1) 0%, rgba(6,14,24,0.99) 48%, rgba(8,16,26,0.92) 62%, rgba(8,16,26,0.0) 72%)' }}
      />
      {/* Right lighter panel — only right 42% */}
      <div
        className="absolute top-0 right-0 bottom-0 z-[3] hidden lg:block"
        style={{ width: '42%', background: 'linear-gradient(to left, rgba(4,10,18,0.2) 0%, rgba(4,10,18,0.6) 70%, rgba(4,10,18,0.95) 100%)' }}
      />

      {/* Mobile overlay */}
      <div className="absolute inset-0 z-[2] lg:hidden" style={{ background: 'rgba(4,10,18,0.94)' }} />

      {/* Gold top accent bar */}
      <div className="absolute top-0 left-0 right-0 h-[3px] z-20 bg-gold" />

      {/* ── VERTICAL LEFT EDGE STRIPE ───────────────────────── */}
      <div
        className="absolute left-0 top-0 bottom-0 w-[3px] z-20 hidden lg:block"
        style={{ background: 'linear-gradient(180deg, transparent 0%, rgba(201,168,76,0.7) 20%, rgba(201,168,76,0.5) 80%, transparent 100%)' }}
      />

      {/* ── VERTICAL SPLIT DIVIDER ──────────────────────────── */}
      <div
        className="absolute top-0 bottom-0 z-[5] hidden xl:block"
        style={{ left: '59%', width: '1px', background: 'linear-gradient(180deg, transparent 5%, rgba(201,168,76,0.35) 25%, rgba(201,168,76,0.25) 75%, transparent 95%)' }}
      />

      {/* ── RIGHT PANEL: Dramatic Image Card ────────────────── */}
      <div className="absolute right-0 top-0 bottom-0 z-[4] hidden xl:flex flex-col justify-end" style={{ width: '39%' }}>
        <div className="relative w-full h-full overflow-hidden">
          {/* Right panel portrait image */}
          <img
            src="https://readdy.ai/api/search-image?query=close%20up%20dramatic%20underground%20sewer%20pipe%20large%20diameter%20concrete%20tunnel%20interior%20atmospheric%20moody%20editorial%20photography%20dark%20cinematic%20premium%20commercial%20deep%20perspective%20vanishing%20point%20no%20people%20no%20logos%20no%20text%20no%20branding&width=800&height=1100&seq=hero-v5-panel-infra&orientation=portrait"
            alt="Professional drain and sewer service infrastructure"
            className="absolute inset-0 w-full h-full object-cover"
            style={{ objectPosition: 'center top', filter: 'brightness(0.62) contrast(1.12) saturate(0.72)' }}
          />
          {/* Gradient fade from left */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{ background: 'linear-gradient(to right, rgba(4,10,18,0.88) 0%, rgba(4,10,18,0.32) 38%, rgba(4,10,18,0.06) 72%, rgba(4,10,18,0.12) 100%)' }}
          />
          {/* Gradient top fade */}
          <div
            className="absolute top-0 left-0 right-0 h-40 pointer-events-none"
            style={{ background: 'linear-gradient(to bottom, rgba(4,10,18,0.7) 0%, transparent 100%)' }}
          />
          {/* Gradient bottom fade */}
          <div
            className="absolute bottom-0 left-0 right-0 h-48 pointer-events-none"
            style={{ background: 'linear-gradient(to top, rgba(4,10,18,0.85) 0%, transparent 100%)' }}
          />

          {/* ── Floating credential card ──────────────────── */}
          <div
            className="absolute bottom-14 right-8 z-10 flex flex-col gap-2.5"
            style={{ width: '210px' }}
          >
            <div
              className="px-5 py-4 rounded-2xl"
              style={{ background: 'rgba(6,14,24,0.92)', border: '1px solid rgba(201,168,76,0.3)', backdropFilter: 'blur(20px)' }}
            >
              <div className="flex items-center gap-2 mb-3">
                <i className="ri-star-fill text-gold text-sm" />
                <span className="font-serif text-white font-bold text-[1.5rem] leading-none">4.9</span>
                <span className="text-white/65 text-[11px] font-sans ml-1">/ 5.0</span>
              </div>
              <div className="text-white/70 text-[11px] font-sans">500+ Verified Google Reviews</div>
              <div className="h-px my-3 bg-white/10" />
              <div className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 bg-green-400 rounded-full flex-shrink-0" />
                <span className="text-white/75 text-[11px] font-sans">Available Now — 24/7</span>
              </div>
            </div>
            {[
              { icon: 'ri-shield-check-fill', label: 'CA Licensed & Insured' },
              { icon: 'ri-award-line', label: '10+ Years Experience' },
            ].map(b => (
              <div
                key={b.label}
                className="flex items-center gap-3 px-4 py-3 rounded-xl"
                style={{ background: 'rgba(6,14,24,0.86)', border: '1px solid rgba(201,168,76,0.2)', backdropFilter: 'blur(16px)' }}
              >
                <i className={`${b.icon} text-gold/80 text-sm flex-shrink-0`} />
                <span className="text-white/80 text-[11px] font-sans">{b.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── LEFT MAIN CONTENT ───────────────────────────────── */}
      <div className="relative z-10 w-full flex items-center">
        <div className="w-full max-w-[1280px] mx-auto px-8 xl:px-12 pt-40 pb-24">
          <div className="xl:max-w-[56%]">

            {/* Eyebrow */}
            <div className="flex items-center gap-4 mb-10 animate-fade-in">
              <div className="h-px w-16 bg-gold" />
              <span className="text-gold/85 text-[10px] tracking-[0.38em] uppercase font-sans font-semibold">
                {eyebrow}
              </span>
            </div>

            {/* Massive headline */}
            <h1
              className="font-serif text-white font-bold leading-[1.02] mb-9 animate-fade-in-up text-shadow-dark"
              style={{ fontSize: 'clamp(3.8rem, 6.4vw, 6.8rem)', letterSpacing: '-0.01em' }}
            >
              {headlineLines.map((line, i) => (
                <span
                  key={i}
                  className={
                    i === headlineLines.length - 1
                      ? 'italic text-gold-light block'
                      : 'text-white block'
                  }
                >
                  {line}
                </span>
              ))}
            </h1>

            {/* Gold cascade rule */}
            <div className="flex items-center gap-3 mb-9 animate-fade-in delay-200">
              <div className="h-[3px] w-24 bg-gold rounded-full" />
              <div className="h-[3px] w-8 rounded-full" style={{ background: 'rgba(201,168,76,0.38)' }} />
              <div className="h-[3px] w-4 rounded-full" style={{ background: 'rgba(201,168,76,0.18)' }} />
            </div>

            {/* Subheadline */}
            <p
              className="font-sans text-white/90 leading-[2.1] mb-14 animate-fade-in-up delay-200"
              style={{ fontSize: 'clamp(15px, 1.4vw, 17px)', maxWidth: '520px' }}
            >
              {subheadline}
            </p>

            {/* CTA row */}
            <div className="flex flex-wrap items-center gap-4 mb-16 animate-fade-in-up delay-300">
              <Link
                to={primaryCTA.link}
                className="relative bg-gold hover:bg-gold-mid text-white font-bold text-[11px] px-11 py-[18px] rounded transition-all duration-300 cursor-pointer whitespace-nowrap tracking-[0.2em] uppercase flex items-center gap-3 group overflow-hidden"
              >
                <span className="relative z-10">{primaryCTA.text}</span>
                <i className="ri-arrow-right-line relative z-10 text-base transition-transform duration-300 group-hover:translate-x-1.5" />
                <span className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </Link>
              <a
                href={secondaryCTA.href}
                className="flex items-center gap-3 border-2 border-white/30 hover:border-gold/60 text-white/90 hover:text-gold font-bold text-[11px] px-11 py-[17px] rounded transition-all duration-300 cursor-pointer whitespace-nowrap tracking-[0.2em] uppercase group"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-phone-line text-sm" />
                </div>
                {secondaryCTA.text}
              </a>
            </div>

            {/* Horizontal divider */}
            <div
              className="h-px max-w-[480px] mb-9 animate-fade-in delay-400"
              style={{ background: 'linear-gradient(90deg, rgba(201,168,76,0.55), rgba(201,168,76,0.12) 65%, transparent)' }}
            />

            {/* Trust badges */}
            <div className="flex flex-wrap items-center gap-x-8 gap-y-3.5 animate-fade-in delay-500">
              {badges.map(badge => (
                <div key={badge.label} className="flex items-center gap-2.5 group cursor-default">
                  <div className="w-[15px] h-[15px] flex items-center justify-center text-gold/80 group-hover:text-gold transition-colors">
                    <i className={`${badge.icon} text-xs`} />
                  </div>
                  <span className="text-white/65 text-[10.5px] tracking-[0.2em] font-sans uppercase group-hover:text-white/90 transition-colors">
                    {badge.label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom fade — into white, kept below content zone */}
      <div
        className="absolute bottom-0 left-0 right-0 h-20 z-[6] pointer-events-none"
        style={{ background: 'linear-gradient(to bottom, transparent 0%, rgba(255,255,255,0.6) 75%, #ffffff 100%)' }}
      />

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-[7] hidden lg:flex flex-col items-center gap-2 animate-fade-in delay-700">
        <div className="w-px h-12" style={{ background: 'linear-gradient(to bottom, rgba(201,168,76,0.6), transparent)' }} />
        <span className="text-white/18 text-[9px] tracking-[0.38em] uppercase font-sans">Scroll</span>
      </div>
    </section>
  );
};

export default HeroSection;
